import { useContext } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { pushLeadEventToDataLayer, pushSupportCaseGAEventToDataLayer } from '../../common/analytics.js';
import { dsClass } from '../../common/constants/dsClasses.js';
import { paths } from '../../common/constants/pathVariables.js';
import { t, yourMessageWasSentMsg } from '../../common/i18n/index.js';
import { SupportCase } from '../../generated/api/models.js';
import { useAuth } from '../../public/site/AuthProvider.js';
import { SiteContext } from '../../public/site/SiteContext.js';
import { addNotification } from '../../public/site/notificationStore.js';
import { ContactSupportForm } from '../ContactSupportForm/index.js';
import { Loading } from '../Loading/index.js';
import type { Address, BillChannel, CompanyInfoResponse } from '../../generated/api/models.js';
import type { AuthenticatedUser } from '../../public/site/authState';
import type { SupportCaseContactInfo } from '../ContactSupportForm/index.js';

import './NewSupportCase.scss';

export interface NewSupportCaseProps {
  selfService: boolean;
  title?: string;
  billChannels: BillChannel[];
  companyInfo?: CompanyInfoResponse;
}

const NoeSupportCaseInfo = () => {
  return (
    <>
      <h3 className={dsClass.H3_BOOK}>{t.IYBA('Follow support case progress')}</h3>
      <p>
        {t.YAA5(
          'Your support case will be visible in Support Cases page and it will be visible to all OmaElisa users in your company.'
        )}
      </p>
      <p>
        {t.YNUS('You can follow support case progress on')}{' '}
        <Link to={paths.MESSAGE_CENTER_SUPPORT_CASES}>{t.RN44('Support Cases page')}</Link>
        {'. '}
        {t.NXHY('You will always receive replies in your email as well.')}
      </p>
    </>
  );
};

const PublicSupportCaseInfo = () => {
  return (
    <>
      <h3 className={dsClass.H3_BOOK}>{t.W52I('Want smoother experience?')}</h3>
      <p>
        <Link to={paths.REGISTER_PAGE}>{t.XE0P('Create an account')}</Link>{' '}
        {t.QG2G('for OmaElisa for Companies so we can give you a more personal service.')}
      </p>
      <p>
        {t.DB2P(
          'In OmaElisa for Companies you can send attachments with your request and track the progress of your support requests.'
        )}
      </p>
    </>
  );
};

const supportCaseContactInfo = (
  { firstName, lastName, mobile, email, businessId, companyName, mdmId }: AuthenticatedUser,
  { line1, line2, postalCode, postOffice }: Address
): SupportCaseContactInfo | undefined => {
  return lastName && mobile && email && businessId && mdmId
    ? {
        firstName: firstName,
        lastName: lastName,
        phoneNumber: mobile,
        email: email,
        businessId: businessId,
        businessName: companyName,
        masterId: mdmId,
        postalAddress: [line1, line2].filter(Boolean).join(' '),
        postCode: postalCode,
        postOffice: postOffice,
      }
    : undefined;
};
export const NewSupportCase = ({ selfService, title, billChannels, companyInfo }: NewSupportCaseProps) => {
  const navigate = useNavigate();
  const { siteBaseUrl } = useContext(SiteContext);
  const { pathname } = useLocation();
  const { authenticatedUser } = useAuth();

  const contactInfo =
    authenticatedUser && companyInfo?.address && supportCaseContactInfo(authenticatedUser, companyInfo.address);

  const onSendCaseAuthenticated = (feature: SupportCase.FeatureEnum) => {
    addNotification(t.Q61D(yourMessageWasSentMsg), 'success');

    if (feature === SupportCase.FeatureEnum.CONTACT_INFO_OFFER_REQUEST) {
      pushLeadEventToDataLayer('ONLINE_CONTACT', false);
      navigate(paths.CONTACT_THANK_YOU);
    } else {
      pushSupportCaseGAEventToDataLayer('form_submit', true, feature);
      navigate(paths.MESSAGE_CENTER_SUPPORT_CASES);
    }
  };

  const onSendCaseAnonymous = (feature: SupportCase.FeatureEnum) => {
    if (feature === SupportCase.FeatureEnum.CONTACT_INFO_OFFER_REQUEST) {
      pushLeadEventToDataLayer('ONLINE_CONTACT', true);
    } else {
      pushSupportCaseGAEventToDataLayer('form_submit', false, feature);
    }
    navigate(paths.CONTACT_THANK_YOU);
  };

  if (selfService && !contactInfo) {
    return <Loading />;
  } else {
    return (
      <div className={`of-new-support-case ${dsClass.MARGIN_VERTICAL_8}`}>
        <div className="contact-form">
          {title && <h2>{title}</h2>}
          <ContactSupportForm
            selfService={selfService && contactInfo !== undefined}
            sourceUrl={`${siteBaseUrl}${pathname}`}
            onSendCaseAuthenticated={onSendCaseAuthenticated}
            onSendCaseAnonymous={onSendCaseAnonymous}
            contactInfo={contactInfo}
            billChannels={billChannels}
          />
        </div>
        <div className="info">
          <div className={`image image--${selfService ? 'noe' : 'public'}`}></div>
          <div className="info-text">{selfService ? <NoeSupportCaseInfo /> : <PublicSupportCaseInfo />}</div>
        </div>
      </div>
    );
  }
};
